import React, { useState, useEffect } from 'react';
import api from '../api';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { PageHeader } from '../components/common';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '../components/ui/dialog';

const VexsoftConfig = () => {
  const [configs, setConfigs] = useState([]);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [editingConfig, setEditingConfig] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    apiKey: '',
    token: '',
  });

  useEffect(() => {
    loadConfigs();
  }, []);

  const loadConfigs = async () => {
    try {
      const response = await api.get('/vexsoft/configs');
      setConfigs(response.data);
    } catch (error) {
      setError('Erro ao carregar configurações');
    }
  };

  const handleOpen = (config = null) => {
    if (config) {
      setFormData({
        name: config.name,
        apiKey: config.apiKey,
        token: config.token,
      });
      setEditingConfig(config);
    } else {
      setFormData({
        name: '',
        apiKey: '',
        token: '',
      });
      setEditingConfig(null);
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setFormData({
      name: '',
      apiKey: '',
      token: '',
    });
    setEditingConfig(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingConfig) {
        await api.put(`/vexsoft/configs/${editingConfig._id}`, formData);
        setSuccess('Configuração atualizada com sucesso!');
      } else {
        await api.post('/vexsoft/configs', formData);
        setSuccess('Configuração criada com sucesso!');
      }
      loadConfigs();
      handleClose();
    } catch (error) {
      setError(error.response?.data?.message || 'Erro ao salvar configuração');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Tem certeza que deseja excluir esta configuração?')) {
      try {
        await api.delete(`/vexsoft/configs/${id}`);
        setSuccess('Configuração excluída com sucesso!');
        loadConfigs();
      } catch (error) {
        setError('Erro ao excluir configuração');
      }
    }
  };

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="space-y-6">
        <PageHeader
          title="Configurações da Vexsoft"
          description="Gerencie as integrações com a API da Vexsoft"
        >
          <Button
            variant="default"
            onClick={() => handleOpen()}
            className="flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            Nova Configuração
          </Button>
        </PageHeader>

        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription className="flex justify-between items-center">
              {error}
              <Button variant="ghost" size="sm" className="h-8 px-2" onClick={() => setError(null)}>
                Fechar
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {success && (
          <Alert className="mb-4 border-green-200 bg-green-50 text-green-800">
            <AlertDescription className="flex justify-between items-center">
              {success}
              <Button variant="ghost" size="sm" className="h-8 px-2" onClick={() => setSuccess(null)}>
                Fechar
              </Button>
            </AlertDescription>
          </Alert>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {configs.map((config) => (
            <Card key={config._id} className="p-4 border">
              <div className="flex justify-between items-center mb-3">
                <h3 className="font-medium text-lg">{config.name}</h3>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="icon"
                    onClick={() => handleOpen(config)}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button 
                    variant="outline" 
                    size="icon"
                    className="text-red-500 hover:text-red-600"
                    onClick={() => handleDelete(config._id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <p className="text-sm text-muted-foreground">
                Token: {config.token}
              </p>
              <p className="text-sm text-muted-foreground">
                API Key: {config.apiKey}
              </p>
            </Card>
          ))}
        </div>

        <Dialog open={open} onOpenChange={() => handleClose()}>
          <DialogContent className="sm:max-w-[500px]">
            <form onSubmit={handleSubmit}>
              <DialogHeader>
                <DialogTitle>
                  {editingConfig ? 'Editar Configuração' : 'Nova Configuração'}
                </DialogTitle>
              </DialogHeader>
              <div className="grid gap-4 py-4">
                <div className="grid gap-2">
                  <Label htmlFor="name">Nome da Locadora</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    required
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="apiKey">API Key</Label>
                  <Input
                    id="apiKey"
                    value={formData.apiKey}
                    onChange={(e) =>
                      setFormData({ ...formData, apiKey: e.target.value })
                    }
                    required
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="token">Token</Label>
                  <Input
                    id="token"
                    value={formData.token}
                    onChange={(e) =>
                      setFormData({ ...formData, token: e.target.value })
                    }
                    required
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" type="button" onClick={handleClose}>
                  Cancelar
                </Button>
                <Button type="submit">Salvar</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default VexsoftConfig;
